import { useObserverExperimentReporter } from '@client/hooks/use-observer-experiment-reporter';
import { newCopySelector } from '@client/redux/experiments/selectors/new-copy';
import { isReviewFlowExitModalV2Selector } from '@client/redux/experiments/selectors/review-flow-exit-modal-v2';
import { reviewPhotoFilterSelector } from '@client/redux/experiments/selectors/review-photo-filter';
import { reviewsLayoutAlignmentSelector } from '@client/redux/experiments/selectors/reviews-layout-alignment';
import { CloseModal } from '@components/CloseModal';
import {
	googleReviewsAtom,
	isFetchingAtom,
	totalCountAtom,
} from '@components/reviews/atoms';
import ReviewsModalVerTwo from '@components/reviews/components/reviews_modal_ver_two';
import { SearchSortFilter } from '@components/reviews/components/search-sort-filter';
import { shouldShowTextAtom } from '@components/reviews/components/search-sort-filter/atoms';
import {
	amountToRenderAtom,
	reviewsCollectionAtom,
} from '@components/reviews/shared-atoms';
import { ReviewPhotosFilmstrip } from '@containers/review-photos/ReviewPhotosFilmstrip';
import { afterNextPaint } from '@helpers/after-next-paint';
import { useLiteStorefront } from '@hooks/use-lite-storefront';
import type { NavItemProps } from '@pages/Storefront/containers/nav/types';
import { ReviewPromptBox } from '@pages/Storefront/containers/reviews/components/review_prompt_box';
import { GoogleReviewsToggleSelector } from '@pages/Storefront/containers/reviews/components/reviews/reviews_with_google_reviews/components/google_reviews_toggle_selector/GoogleReviewsToggleSelector';
import { GoogleReviewsWrapper } from '@pages/Storefront/containers/reviews/components/reviews/reviews_with_google_reviews/components/google_reviews_wrapper/GoogleReviewsWrapper';
import { useAppSelector } from '@redux/hooks';
import { experiments } from '@settings';
import { Spinner } from '@tkww/orion-web-ds-feedback';
import { TextTitle } from '@tkww/orion-web-ds-text';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import React, {
	type FC,
	memo,
	useMemo,
	useState,
	useRef,
	useEffect,
} from 'react';
import type { Decorated } from 'types/vendor';
import VendorAnalytics from '../../../../../../decorators/vendorAnalytics';
import Review from '../review/Review';
import ReviewsSummary from '../reviews_summary';
import VerifiedReviewsBox from '../verified_reviews_box';
import Styles from './Reviews.scss';
import { ViewMoreReviews } from './components/view_more_reviews';
import { useOpenReviewModal } from './hooks/useOpenReviewModal';
import { useSecondaryCTAIndex } from './hooks/useSecondaryCTAIndex';

interface GoogleReviewsResultsProps {
	openModal: () => void;
}

const GoogleReviewsResults = ({ openModal }: GoogleReviewsResultsProps) => {
	const googleReviewsData = useAtomValue(googleReviewsAtom);
	const totalVendorReviewsCount = useAtomValue(totalCountAtom);

	return (
		<>
			{!totalVendorReviewsCount && (
				<ReviewPromptBox
					title="Have you booked this vendor?"
					message="Be the first couple to share your thoughts!"
					handleClick={openModal}
				/>
			)}
			<GoogleReviewsWrapper
				googleReviews={googleReviewsData.reviews}
				googleReviewsLink={googleReviewsData.externalUrl}
			/>
		</>
	);
};

interface TKReviewsResultsProps {
	vendorAnalytics: VendorAnalytics;
	openModal: () => void;
	hasGoogleReviews: boolean;
}

const TKReviewsResults = ({
	vendorAnalytics,
	openModal,
	hasGoogleReviews,
}: TKReviewsResultsProps) => {
	const collection = useAtomValue(reviewsCollectionAtom);
	const isFetching = useAtomValue(isFetchingAtom);
	const totalVendorReviewsCount = useAtomValue(totalCountAtom);
	const amountToRender = useAtomValue(amountToRenderAtom);
	const secondaryCTAIndex = useSecondaryCTAIndex();
	const shouldShowText = useAtomValue(shouldShowTextAtom);
	const reviewsContainerRef = useRef<HTMLDivElement>(null);
	const hasInitialLoadFinished = useRef(false);

	useEffect(() => {
		if (isFetching) return;

		if (!hasInitialLoadFinished.current) {
			hasInitialLoadFinished.current = true;
		} else {
			reviewsContainerRef.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
			});
		}
	}, [isFetching]);

	return (
		<div
			className={classNames(Styles.reviewsWrapper, {
				[Styles.filteredReviews]: shouldShowText,
			})}
		>
			{totalVendorReviewsCount > 0 && (
				<div data-testid="review-search-sort-filter" ref={reviewsContainerRef}>
					<SearchSortFilter />
				</div>
			)}
			{hasGoogleReviews && !totalVendorReviewsCount && (
				<ReviewPromptBox
					title="No reviews yet"
					message="Be the first couple to share your thoughts!"
					handleClick={openModal}
				/>
			)}
			<div>
				{isFetching ? (
					<div className={Styles.ssfLoaderContainer}>
						<div className={Styles.ssfLoader}>
							<Spinner />
						</div>
					</div>
				) : (
					<>
						<div data-testid="review-items" className={Styles.reviewItems}>
							{collection.map((review, index) => {
								return (
									<Review
										key={`review-${review.id}`}
										review={review}
										vendorAnalytics={vendorAnalytics}
										hidden={index >= amountToRender}
										showSecondaryCTA={index === secondaryCTAIndex}
										border={index > 0}
									/>
								);
							})}
						</div>
						<ViewMoreReviews vendorAnalytics={vendorAnalytics} />
					</>
				)}
			</div>
		</div>
	);
};

const ReviewsExperimentReporter: FC = () => {
	const isLiteStorefront = useLiteStorefront();
	const reviewPhotoFilterVariant = useAppSelector(reviewPhotoFilterSelector);
	const reviewsLayoutAlignmentVariant = useAppSelector(
		reviewsLayoutAlignmentSelector,
	);
	const newCopyVariant = useAppSelector(newCopySelector);

	const configs = useMemo(() => {
		return [
			{
				experimentId: experiments.reviewPhotoFilterV2,
				variant: reviewPhotoFilterVariant,
				logMessage: `Review Photo Filter Assignment: ${reviewPhotoFilterVariant}`,
			},
			{
				experimentId: experiments.reviewsLayoutAlignment,
				variant: reviewsLayoutAlignmentVariant,
				entranceCriteria: () => isLiteStorefront,
				logMessage: `Reviews Layout Alignment Assignment: ${reviewsLayoutAlignmentVariant}`,
			},
			{
				experimentId: experiments.newCopy,
				variant: newCopyVariant,
				logMessage: `New Copy Experiment Assignment: ${newCopyVariant}`,
			},
		];
	}, [
		isLiteStorefront,
		reviewPhotoFilterVariant,
		reviewsLayoutAlignmentVariant,
		newCopyVariant,
	]);

	const ref = useObserverExperimentReporter<HTMLDivElement>(configs);

	return (
		<div
			className="reviews-section-intersection-tracker"
			ref={ref}
			aria-hidden="true"
		/>
	);
};

interface ReviewsProps extends NavItemProps {}

export const Reviews: FC<ReviewsProps> = memo(() => {
	const vendor = useAppSelector((state) => state.vendor.vendor) as Decorated;
	const vendorAnalytics = useMemo(() => new VendorAnalytics(vendor), [vendor]);
	const pageType = useAppSelector((state) => state.page.pageType);
	const totalVendorReviewsCount = useAtomValue(totalCountAtom);
	const googleReviewsData = useAtomValue(googleReviewsAtom);

	const [modalIsOpen, setModalIsOpen] = useState(false);
	const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
	const [showTKReviews, setShowTKReviews] = useState(
		totalVendorReviewsCount > 0,
	);
	const isReviewFlowExitModalV2Enabled = useAppSelector(
		isReviewFlowExitModalV2Selector,
	);

	const resetFormRef = useRef<(() => void) | null>(null);
	const isDismissingCloseConfirmation = useRef(false);

	const openModal = useOpenReviewModal(
		setModalIsOpen,
		pageType,
		vendorAnalytics,
	);
	const isLiteStorefront = useLiteStorefront();
	const hasGoogleReviews =
		googleReviewsData && googleReviewsData.reviews.length > 0;
	const handleCloseAttempt = () => {
		if (isDismissingCloseConfirmation.current) return;
		if (isReviewFlowExitModalV2Enabled) {
			setShowCloseConfirmation(true);
		} else {
			handleConfirmClose();
		}
	};

	const handleConfirmClose = () => {
		// Deferred to let the "Leave anyway" click paint (e.g. its pressed state)
		// before the (potentially expensive) modal teardown and form reset run.
		afterNextPaint(() => {
			setShowCloseConfirmation(false);
			setModalIsOpen(false);
			resetFormRef.current?.();
		});
	};

	const handleCancelClose = () => {
		isDismissingCloseConfirmation.current = true;
		setShowCloseConfirmation(false);
		// This timeout is to handle the event propagation and throttle of 200ms from NavigableModal.
		// Without this, if the user clicked "X" on the <CloseModal /> component, after 200ms, the <CloseModal /> would reopen.
		setTimeout(() => {
			isDismissingCloseConfirmation.current = false;
		}, 400);
	};

	return (
		<div>
			{showCloseConfirmation && (
				<CloseModal
					title="Are you sure you want to leave?"
					text="Your review could really help other couples find the right vendor for their day."
					confirmButtonText="Leave anyway"
					cancelButtonText="Keep reviewing"
					onConfirm={handleConfirmClose}
					onCancel={handleCancelClose}
				/>
			)}
			{modalIsOpen && (
				<ReviewsModalVerTwo
					isModalOpen={modalIsOpen}
					onCloseAttempt={handleCloseAttempt}
					onClose={handleConfirmClose}
					resetFormRef={resetFormRef}
					pageType={pageType}
				/>
			)}
			<div className={Styles.reviewsContainer}>
				<div className={Styles.headerContainer}>
					<TextTitle size="xs" id="reviews">
						Reviews
					</TextTitle>
					{!!totalVendorReviewsCount && isLiteStorefront && (
						<VerifiedReviewsBox />
					)}
				</div>
				<div className={Styles.reviewsContent}>
					<ReviewsSummary
						openModal={openModal}
						hasGoogleReviews={hasGoogleReviews}
						vendorAnalytics={vendorAnalytics}
					/>
					<ReviewsExperimentReporter />
					<ReviewPhotosFilmstrip />
					{hasGoogleReviews && (
						<GoogleReviewsToggleSelector
							showTKReviews={showTKReviews}
							setShowTKReviews={setShowTKReviews}
							googleRating={googleReviewsData.rating}
							googleReviewsCount={googleReviewsData.totalReviewCount}
							googleReviewsCountFormatted={
								googleReviewsData.totalReviewCountFormatted
							}
						/>
					)}
					{showTKReviews && (
						<TKReviewsResults
							vendorAnalytics={vendorAnalytics}
							openModal={openModal}
							hasGoogleReviews={hasGoogleReviews}
						/>
					)}
					{!showTKReviews && hasGoogleReviews && (
						<GoogleReviewsResults openModal={openModal} />
					)}
				</div>
			</div>
		</div>
	);
});
